import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { HiOutlineMail } from 'react-icons/hi';
import { BsTelephone } from 'react-icons/bs';
import { AiOutlineLinkedin } from 'react-icons/ai';
import { FiMapPin, FiSend, FiCheckCircle } from 'react-icons/fi';
import { Input, FormControl, FormLabel, Textarea } from '@chakra-ui/react';

import { MainWrapper, Section, Reveal, Loader } from '../../shared';
import { useSettings } from '../../../context/SettingsContext';

import './ContactPage.scss';

// -------------------------------------------------------------
//  Contact form endpoint
//  Posts to the DS-Innovation Firebase Cloud Function, which sends
//  the email server-side. The visitor never leaves the page and no
//  email client is opened; on success they see an in-page confirmation.
//  (If the request ever fails, the error message offers a mailto link.)
// -------------------------------------------------------------
const CONTACT_ENDPOINT = 'https://us-central1-ds-innovation.cloudfunctions.net/contactFormSubmission';
const CONTACT_EMAIL = 'daniel.savin@ds-innovation.dev';
const CONTACT_PHONE = '+32 483 25 66 10';

export function ContactPage() {
  const { t } = useSettings();
  const c = t.contact;
  const { redirectReason } = useParams();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [business, setBusiness] = useState('');
  const [message, setMessage] = useState(redirectReason ? `${c.form.enquiryPrefix}${redirectReason}\n\n` : '');

  const [isLoading, setIsLoading] = useState(false);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [invalid, setInvalid] = useState({ firstName: false, lastName: false, email: false, message: false });

  const buildMailto = () => {
    const subject = encodeURIComponent(`${c.eyebrow}: ${firstName} ${lastName}`.trim());
    const body = encodeURIComponent(
      `${c.form.firstName}: ${firstName} ${lastName}\n${c.form.email}: ${email}\n${c.form.phone}: ${phone}\n${c.form.business}: ${business}\n\n${message}`
    );
    return `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const nextInvalid = {
      firstName: firstName.trim() === '',
      lastName: lastName.trim() === '',
      email: !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()),
      message: message.trim() === '',
    };
    setInvalid(nextInvalid);
    setErrorMessage('');

    if (Object.values(nextInvalid).some(Boolean)) {
      setErrorMessage(c.form.validation);
      return;
    }

    // Send the message via the Cloud Function. No email client, no page leave.
    setIsLoading(true);
    try {
      const response = await fetch(CONTACT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firstName, lastName, email, phone, organization: business, message }),
      });

      if (response.ok) {
        setIsSuccessful(true);
      } else {
        setErrorMessage(c.form.error);
      }
    } catch {
      setErrorMessage(c.form.error);
    } finally {
      setIsLoading(false);
    }
  };

  const details = [
    { icon: <HiOutlineMail aria-hidden="true" />, label: c.labels.email, value: CONTACT_EMAIL, href: `mailto:${CONTACT_EMAIL}` },
    { icon: <BsTelephone aria-hidden="true" />, label: c.labels.phone, value: CONTACT_PHONE, href: 'tel:+32483256610' },
    {
      icon: <AiOutlineLinkedin aria-hidden="true" />,
      label: c.labels.linkedin,
      value: 'company/ds-innovation',
      href: 'https://www.linkedin.com/company/ds-innovation',
    },
    { icon: <FiMapPin aria-hidden="true" />, label: c.labels.location, value: c.locationValue, href: undefined },
  ];

  return (
    <MainWrapper currentPageName="Contact" headerPageText={c.headerTitle} eyebrow={c.eyebrow} intro={c.intro}>
      <Section ariaLabel={c.detailsTitle}>
        <div className="contact">
          {/* Details */}
          <Reveal className="contact__details">
            <h2>{c.detailsTitle}</h2>
            <p>{c.detailsBody}</p>
            <ul className="contact__list">
              {details.map((d) => (
                <li key={d.label}>
                  <span className="contact__icon">{d.icon}</span>
                  <span className="contact__meta">
                    <span className="contact__label">{d.label}</span>
                    {d.href ? (
                      <a
                        href={d.href}
                        {...(d.href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                      >
                        {d.value}
                      </a>
                    ) : (
                      <span>{d.value}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
            <div className="contact__reassure">
              <FiCheckCircle aria-hidden="true" />
              <span>{c.reassure}</span>
            </div>
          </Reveal>

          {/* Form */}
          <Reveal delay={0.1} className="contact__form-card ds-card">
            {isSuccessful ? (
              <div className="contact__success" role="status">
                <FiCheckCircle aria-hidden="true" />
                <h3>{c.form.successTitle}</h3>
                <p>{c.form.successBody}</p>
              </div>
            ) : isLoading ? (
              <div className="contact__loading">
                <Loader />
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                <div className="contact__row">
                  <FormControl isRequired isInvalid={invalid.firstName}>
                    <FormLabel>{c.form.firstName}</FormLabel>
                    <Input
                      placeholder={c.form.firstNamePh}
                      autoComplete="given-name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                  </FormControl>
                  <FormControl isRequired isInvalid={invalid.lastName}>
                    <FormLabel>{c.form.lastName}</FormLabel>
                    <Input
                      placeholder={c.form.lastNamePh}
                      autoComplete="family-name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                  </FormControl>
                </div>

                <div className="contact__row">
                  <FormControl isRequired isInvalid={invalid.email}>
                    <FormLabel>{c.form.email}</FormLabel>
                    <Input
                      type="email"
                      placeholder={c.form.emailPh}
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>{c.form.phone}</FormLabel>
                    <Input
                      type="tel"
                      placeholder={c.form.phonePh}
                      autoComplete="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  </FormControl>
                </div>

                <FormControl>
                  <FormLabel>{c.form.business}</FormLabel>
                  <Input
                    type="text"
                    placeholder={c.form.businessPh}
                    autoComplete="organization"
                    value={business}
                    onChange={(e) => setBusiness(e.target.value)}
                  />
                </FormControl>

                <FormControl isRequired isInvalid={invalid.message} mt={4}>
                  <FormLabel>{c.form.whatInMind}</FormLabel>
                  <Textarea
                    rows={6}
                    placeholder={c.form.messagePh}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                </FormControl>

                {errorMessage && (
                  <div className="contact__error" role="alert">
                    {errorMessage} <a href={buildMailto()}>{c.form.errorLink} →</a>
                  </div>
                )}

                <button type="submit" className="ds-btn ds-btn--primary contact__submit">
                  {c.form.send} <FiSend aria-hidden="true" className="ds-btn__icon" />
                </button>
                <p className="contact__fallback">
                  {c.form.fallbackPre}
                  <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
                </p>
              </form>
            )}
          </Reveal>
        </div>
      </Section>
    </MainWrapper>
  );
}

export default ContactPage;
